import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetTicketsQuery as GetTickets } from '__generated__/graphql';

import { DateTimeForm } from '../../../Common/DateRangeForm';
import { TranslatableInput } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeleteTicketCategoryMutation from './DeleteTicketCategoryMutation';
import EditTicketCategoryMutation from './EditTicketCategoryMutation';
import GetTicketsQuery from './GetTicketsQuery';
import UI from '../../../../common/UI';
import useGroupedSportTypes from '../../useGroupedSportTypes';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetTickets['project']['event'];
type TicketCategory = Event['ticket_categories'][0];

const editTicketCategorySchema = yup.object().shape({
  title: yup.string().required(),
  sport_type: yup.string().required(),
});

export interface EditTicketCategoryFormProps {
  ticketCategory: TicketCategory;
  event: Event;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const EditTicketCategoryForm = ({
  ticketCategory, event, onSuccess, refetchQueries = [GetTicketsQuery],
}: EditTicketCategoryFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();
  const integerInput = useIntegerInput();

  const form = useForm({
    values: {
      title: ticketCategory.title,
      title_translations: translationsForAttribute(ticketCategory.translations, 'title'),
      sport_type: ticketCategory.sport_type,
      start_date: ticketCategory.start_date,
      capacity: ticketCategory.capacity,
    },
  });

  const [editTicketCategory, { error, loading }] = useMutation(
    EditTicketCategoryMutation, {
      variables: {
        input: {
          id: ticketCategory.id,
          ...form.values,
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.ticket_category_saved'));
        onSuccess?.();
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const { valid, errors } = useValidation({
    schema: editTicketCategorySchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const [deleteTicketCategory] = useMutation(
    DeleteTicketCategoryMutation,
    {
      variables: {
        id: ticketCategory.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.ticket_category_deleted'));
        onSuccess?.();
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const { popularSportTypes, otherSportTypes } = useGroupedSportTypes();

  return (
    <UI.GridContainer>
      <UI.SectionHeading>
        {t('edit_ticket_category')}
      </UI.SectionHeading>
      <UI.Card sc={{ spacing: 'xl' }}>
        <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
          <UI.Nav>
            <UI.NavLink
              to={`/${project.id}/events/${event.id}/tickets/categories/${ticketCategory.id}`}
              exact
              resetScrollPosition={false}
            >
              {t('basic_info')}
            </UI.NavLink>
            {ticketCategory.deletable && (
              <UI.ConfirmButton
                onConfirm={() => deleteTicketCategory()}
                sc={{ color: 'error' }}
                loading={loading}
                render={(start, disabled) => (
                  <UI.Button
                    onClick={() => start()}
                    sc={{ color: 'error' }}
                    disabled={disabled}
                  >
                    {t('delete')}
                  </UI.Button>
                )}
              />
            )}
          </UI.Nav>

          <UI.Form onSubmit={() => editTicketCategory()}>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.InputGroup sc={{ valid: !errors.title, touched: form.getTouched('title') }}>
                <UI.InputLabel htmlFor="ticket_category_title">
                  {t('ticket_category_title')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <TranslatableInput
                  form={form}
                  errors={errors}
                  name="title"
                  id="ticket_category_title"
                  defaultLocale={ticketCategory.locale}
                  title={t('ticket_category_title')}
                  maxLength={50}
                />
                <UI.ErrorMessages attribute={t('ticket_category_title')} errors={errors.title} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.sport_type, touched: form.touched.sport_type }}>
                <UI.InputLabel htmlFor="sport_type">
                  {t('sport_type')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.Select id="sport_type" {...form.select('sport_type')}>
                  {!form.values.sport_type && <option value=""> </option>}
                  <optgroup label={t('popular')}>
                    {popularSportTypes.map((category: string, index: number) => (
                      <option {...form.option(category)} key={index}>
                        {category && t(`common:sport_types.${category}`)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('other')}>
                    {otherSportTypes.map((category: string, index: number) => (
                      <option {...form.option(category)} key={index}>
                        {category && t(`common:sport_types.${category}`)}
                      </option>
                    ))}
                  </optgroup>
                </UI.Select>
                <UI.ErrorMessages attribute={t('sport_type')} errors={errors.sport_type} />
              </UI.InputGroup>

              <DateTimeForm
                form={form}
                errors={errors}
                keys={{ date: 'start_date', time: 'start_time' }}
                labels={{ date: t('event_date'), time: t('time') }}
                require={false}
              />

              <UI.InputGroup sc={{ valid: !errors.capacity, touched: form.getTouched('capacity') }}>
                <UI.InputLabel htmlFor="ticket_category_capacity">
                  {t('max_number_registrations')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('ticket_category_capacity_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('capacity', { inputHandler: integerInput() })}
                  format={integerInput().format}
                  maxLength={10}
                  id="ticket_category_capacity"
                  inputMode="numeric"
                  match={/[\d]/g}
                  placeholder="∞"
                />
                <UI.ErrorMessages attribute={t('max_number_registrations')} errors={errors.title} />
              </UI.InputGroup>

              <UI.HR />

              <UI.FormButtons
                form={form}
                submitLabel={t('save_ticket_category')}
                loading={loading}
                valid={valid}
              />
            </UI.FormGrid>
          </UI.Form>
        </UI.FormGrid>
      </UI.Card>
    </UI.GridContainer>
  );
};

export default EditTicketCategoryForm;
